import React, { useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  TextField,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
  FormLabel,
} from '@mui/material';
import { OrderProduct } from '../../types/order';
import PlanCarousel, { ProductFieldConfigItem } from './PlanCarousel';

interface VoiceServiceSelectionProps {
  customerType: 'residential' | 'business';
  voicePlans: OrderProduct[];
  selectedPlan: OrderProduct | null;
  voiceChannels: number;
  portingNumber: string;
  portingDocument: string;
  voiceServiceSelected: boolean;
  onServiceToggle: (selected: boolean) => void;
  onPlanSelect: (plan: OrderProduct | null) => void;
  onChannelsChange: (channels: number) => void;
  onPortingNumberChange: (number: string) => void;
  onPortingDocumentChange: (document: string) => void;
  /** Product fields config for voice plan cards (from Product Fields tab for voice/residential). */
  voiceProductFieldsConfig?: ProductFieldConfigItem[];
}

function VoiceServiceSelection({
  customerType,
  voicePlans,
  selectedPlan,
  voiceChannels,
  portingNumber,
  portingDocument,
  voiceServiceSelected,
  onServiceToggle,
  onPlanSelect,
  onChannelsChange,
  onPortingNumberChange,
  onPortingDocumentChange,
  voiceProductFieldsConfig,
}: VoiceServiceSelectionProps) {
  const [portingYes, setPortingYes] = useState(false);
  // Hide voice service for business customers
  if (customerType === 'business') {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Voice Service
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Voice services are currently only available for residential customers.
        </Typography>
      </Box>
    );
  }
  
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a PDF, JPG, or PNG file');
        return;
      }
      
      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }
      
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        onPortingDocumentChange(base64String);
      };
      reader.readAsDataURL(file);
    }
  };
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Voice Service (Optional)
      </Typography>
      
      <FormControlLabel
        control={
          <Switch
            checked={voiceServiceSelected}
            onChange={(e) => onServiceToggle(e.target.checked)}
          />
        }
        label="Add voice service to my account"
        sx={{ mb: 3 }}
      />
      
      {voiceServiceSelected && (
        <Box sx={{ mt: 3 }}>
          {Array.isArray(voicePlans) && voicePlans.length > 0 ? (
            <PlanCarousel
              plans={voicePlans}
              selectedPlan={selectedPlan}
              onSelectPlan={(plan) => onPlanSelect(plan)}
              subcategoryName="Voice - Residential"
              productFieldsConfig={voiceProductFieldsConfig}
              fieldLabelOverrides={{ description: 'Inclusions' }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No voice plans available at the moment.
            </Typography>
          )}

          {selectedPlan && (
            <>
              <FormControl component="fieldset" sx={{ mt: 4, mb: 3 }}>
                <FormLabel component="legend">Number of Channels</FormLabel>
                <RadioGroup
                  row
                  value={voiceChannels}
                  onChange={(e) => onChannelsChange(parseInt(e.target.value))}
                >
                  <FormControlLabel value={1} control={<Radio />} label="1 Channel" />
                  <FormControlLabel value={2} control={<Radio />} label="2 Channels" />
                </RadioGroup>
              </FormControl>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Porting Existing Number?
                </Typography>
                <RadioGroup
                  row
                  value={portingYes ? 'yes' : 'no'}
                  onChange={(e) => {
                    const yes = e.target.value === 'yes';
                    setPortingYes(yes);
                    if (!yes) {
                      onPortingNumberChange('');
                      onPortingDocumentChange('');
                    }
                  }}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes, port my number" />
                  <FormControlLabel value="no" control={<Radio />} label="No, new number" />
                </RadioGroup>
              </Box>
              
              {portingYes && (
                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Phone Number to Port"
                    value={portingNumber}
                    onChange={(e) => onPortingNumberChange(e.target.value)}
                    placeholder="e.g., 0412345678"
                    sx={{ mb: 2 }}
                  />
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Upload a recent bill showing your current provider and account number
                  </Typography>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileUpload}
                    style={{ marginTop: 8 }}
                  />
                  {portingDocument && (
                    <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                      ✓ Document uploaded
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

export default VoiceServiceSelection;

